import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { useSession } from "../../app/session.js";
import { trpc } from "../../app/trpc.js";

/**
 * Push-подписка этого устройства: общий код для кабинета и страницы уведомлений.
 * Подписка принадлежит браузеру, а не человеку: на общем телефоне салона её надо переводить на того, кто вошёл.
 */

export const pushSupported = (): boolean =>
  typeof window !== "undefined" && "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;

export function urlBase64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const raw = atob((base64 + padding).replace(/-/g, "+").replace(/_/g, "/"));
  const out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

/** Подписка оформлена с этим ключом VAPID? После смены ключей на сервере старая подписка бесполезна (403). */
export function sameServerKey(sub: PushSubscription, publicKey: string): boolean {
  const current = sub.options?.applicationServerKey;
  if (!current) return false;
  const a = new Uint8Array(current);
  const b = urlBase64ToUint8Array(publicKey);
  return a.length === b.length && a.every((x, i) => x === b[i]);
}

/** Регистрация service worker; в dev его нет, поэтому не ждём вечно. */
export async function swRegistration(timeoutMs = 4000): Promise<ServiceWorkerRegistration | null> {
  if (!pushSupported()) return null;
  return Promise.race([navigator.serviceWorker.ready, new Promise<null>((r) => setTimeout(() => r(null), timeoutMs))]);
}

export interface SubscriptionJson {
  endpoint: string;
  keys: { p256dh: string; auth: string };
}

/** Подписка с нужным ключом: старую с другим ключом снимаем и оформляем заново. Разрешение должно быть уже дано. */
export async function ensureSubscription(reg: ServiceWorkerRegistration, publicKey: string): Promise<SubscriptionJson | null> {
  let sub = await reg.pushManager.getSubscription();
  if (sub && !sameServerKey(sub, publicKey)) {
    await sub.unsubscribe();
    sub = null;
  }
  sub ??= await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(publicKey) });
  const json = sub.toJSON();
  if (!json.endpoint || !json.keys?.p256dh || !json.keys.auth) return null;
  return { endpoint: json.endpoint, keys: { p256dh: json.keys.p256dh, auth: json.keys.auth } };
}

/**
 * Снять push-подписку этого устройства при выходе (на общем телефоне салона уведомления прошлого мастера
 * не должны приходить следующему). Возвращает endpoint, чтобы удалить его и на сервере.
 */
export async function dropDevicePush(): Promise<string | null> {
  const reg = await swRegistration(2000);
  const sub = await reg?.pushManager.getSubscription();
  if (!sub) return null;
  const endpoint = sub.endpoint;
  await sub.unsubscribe();
  return endpoint;
}

/**
 * При входе в кабинет: если на устройстве уже есть подписка и разрешение (например, сессия прошлого мастера истекла
 * без «Выйти»), переводим её на вошедшего: сервер сделает upsert по endpoint. Иначе push прошлого человека
 * приходили бы этому, а его собственные никуда. Раз на пользователя за загрузку страницы.
 */
export function useClaimDevicePush(): void {
  const session = useSession();
  const userId = session.user?.id ?? null;
  const key = useQuery(trpc.cabinet.push.publicKey.queryOptions(undefined, { enabled: Boolean(userId), staleTime: Number.POSITIVE_INFINITY }));
  const claim = useMutation(trpc.cabinet.push.subscribe.mutationOptions());
  const done = useRef<string | null>(null);
  const publicKey = key.data?.publicKey ?? null;
  const { mutateAsync } = claim;
  useEffect(() => {
    if (!userId || !publicKey || done.current === userId) return;
    if (!pushSupported() || Notification.permission !== "granted") return;
    done.current = userId;
    void (async () => {
      const reg = await swRegistration();
      if (!reg || !(await reg.pushManager.getSubscription())) return;
      const sub = await ensureSubscription(reg, publicKey);
      if (sub) await mutateAsync(sub);
    })().catch(() => {
      done.current = null;
    });
  }, [userId, publicKey, mutateAsync]);
}
